import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import api from '../api';
import PageHeader from './PageHeader';

const initialForm = {
  nom: '',
  prenom: '',
  email: '',
  password: '',
  etablissement: '',
  role: 'prof'
};

const formatDate = (value) => {
  if (!value) return 'Jamais';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Jamais';

  return date.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

export default function AdminDashboard({ user }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialForm);
  const [editingTeacherId, setEditingTeacherId] = useState('');

  const stats = useMemo(() => {
    const admins = teachers.filter((teacher) => teacher.role === 'admin').length;
    const alreadyConnected = teachers.filter((teacher) => Boolean(teacher.lastLoginAt)).length;
    return {
      total: teachers.length,
      admins,
      alreadyConnected
    };
  }, [teachers]);

  const loadTeachers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/users');
      setTeachers(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de charger les comptes enseignants.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeachers();
  }, []);

  const handleCreateChange = (event) => {
    setCreateForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleEditChange = (event) => {
    setEditForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });

    try {
      await api.post('/users', createForm);
      setFeedback({ type: 'success', message: 'Compte enseignant ajouté.' });
      setCreateOpen(false);
      setCreateForm(initialForm);
      loadTeachers();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.error || 'Création impossible.' });
    }
  };

  const openEditDialog = (teacher) => {
    setEditingTeacherId(teacher._id);
    setEditForm({
      nom: teacher.nom || '',
      prenom: teacher.prenom || '',
      email: teacher.email || '',
      password: '',
      etablissement: teacher.etablissement || '',
      role: teacher.role || 'prof'
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });

    const payload = { ...editForm };
    if (!payload.password) {
      delete payload.password;
    }

    try {
      await api.put(`/users/${editingTeacherId}`, payload);
      setFeedback({ type: 'success', message: 'Compte mis à jour.' });
      setEditOpen(false);
      setEditingTeacherId('');
      loadTeachers();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.error || 'Mise à jour impossible.' });
    }
  };

  const handleDelete = async (teacher) => {
    const confirmed = window.confirm(`Supprimer le compte de ${teacher.prenom} ${teacher.nom} ?`);
    if (!confirmed) return;

    setFeedback({ type: '', message: '' });

    try {
      await api.delete(`/users/${teacher._id}`);
      setFeedback({ type: 'success', message: 'Compte supprimé.' });
      loadTeachers();
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.error || 'Suppression impossible.' });
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <PageHeader
        title="Administration"
        subtitle="Retrouve les comptes inscrits, vérifie leur dernière connexion et gère les accès de l’équipe."
        backTo="/prof"
        homeTo="/"
        actionLabel="Ajouter un enseignant"
        onAction={() => setCreateOpen(true)}
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 5,
          background: 'linear-gradient(135deg, #fff6f7 0%, #f4f6ff 52%, #f7fbff 100%)',
          border: '1px solid rgba(63,61,86,0.08)',
          boxShadow: '0 18px 40px rgba(63,61,86,0.08)'
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Chip icon={<AdminPanelSettingsRoundedIcon />} label="Espace administrateur" sx={{ fontWeight: 700 }} />
            <Chip label={user?.email || 'Compte admin'} variant="outlined" />
          </Stack>

          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Administration des enseignants
          </Typography>

          <Typography color="text.secondary">
            Gère les comptes, les rôles et le suivi de connexion des enseignants.
          </Typography>
        </Stack>
      </Paper>

      {feedback.message && (
        <Alert severity={feedback.type || 'info'} sx={{ mb: 2.5 }}>
          {feedback.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {[
          { label: 'Enseignants', value: stats.total, icon: <GroupRoundedIcon sx={{ color: '#2F5FFF' }} /> },
          { label: 'Admins', value: stats.admins, icon: <AdminPanelSettingsRoundedIcon sx={{ color: '#B42318' }} /> },
          { label: 'Déjà connectés', value: stats.alreadyConnected, icon: <PersonAddAltRoundedIcon sx={{ color: '#0E9F6E' }} /> }
        ].map((card) => (
          <Grid item xs={12} md={4} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: '1px solid rgba(63,61,86,0.08)',
                boxShadow: '0 12px 30px rgba(32,24,64,0.08)'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {card.icon}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {card.value}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 16px 36px rgba(32,24,64,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Établissement</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière connexion</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && teachers.map((teacher) => (
              <TableRow key={teacher._id} hover>
                <TableCell>{teacher.nom}</TableCell>
                <TableCell>{teacher.prenom}</TableCell>
                <TableCell>{teacher.email}</TableCell>
                <TableCell>{teacher.etablissement || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={teacher.role}
                    color={teacher.role === 'admin' ? 'error' : teacher.role === 'formateur' ? 'secondary' : 'primary'}
                    variant={teacher.role === 'admin' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={teacher.lastLoginAt ? 'Déjà connecté' : 'Inscrit'}
                    color={teacher.lastLoginAt ? 'success' : 'default'}
                    variant={teacher.lastLoginAt ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>{formatDate(teacher.lastLoginAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => openEditDialog(teacher)}>
                      Modifier
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineRoundedIcon />}
                      onClick={() => handleDelete(teacher)}
                    >
                      Supprimer
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && teachers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    Aucun enseignant enregistré pour le moment.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    Chargement des comptes enseignants...
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter un compte enseignant</DialogTitle>
        <DialogContent>
          <Box component="form" id="create-teacher-form" onSubmit={handleCreateSubmit} sx={{ mt: 1 }}>
            <TextField margin="normal" fullWidth label="Nom" name="nom" value={createForm.nom} onChange={handleCreateChange} required />
            <TextField margin="normal" fullWidth label="Prénom" name="prenom" value={createForm.prenom} onChange={handleCreateChange} required />
            <TextField margin="normal" fullWidth label="Établissement" name="etablissement" value={createForm.etablissement} onChange={handleCreateChange} required />
            <TextField margin="normal" fullWidth label="Email" name="email" type="email" value={createForm.email} onChange={handleCreateChange} required />
            <TextField margin="normal" fullWidth label="Mot de passe temporaire" name="password" type="password" value={createForm.password} onChange={handleCreateChange} required />
            <TextField margin="normal" fullWidth select label="Rôle" name="role" value={createForm.role} onChange={handleCreateChange}>
              <MenuItem value="prof">prof</MenuItem>
              <MenuItem value="formateur">formateur</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button type="submit" form="create-teacher-form" variant="contained">Créer le compte</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Modifier un compte enseignant</DialogTitle>
        <DialogContent>
          <Box component="form" id="edit-teacher-form" onSubmit={handleEditSubmit} sx={{ mt: 1 }}>
            <TextField margin="normal" fullWidth label="Nom" name="nom" value={editForm.nom} onChange={handleEditChange} required />
            <TextField margin="normal" fullWidth label="Prénom" name="prenom" value={editForm.prenom} onChange={handleEditChange} required />
            <TextField margin="normal" fullWidth label="Établissement" name="etablissement" value={editForm.etablissement} onChange={handleEditChange} required />
            <TextField margin="normal" fullWidth label="Email" name="email" type="email" value={editForm.email} onChange={handleEditChange} required />
            <TextField margin="normal" fullWidth label="Nouveau mot de passe (optionnel)" name="password" type="password" value={editForm.password} onChange={handleEditChange} />
            <TextField margin="normal" fullWidth select label="Rôle" name="role" value={editForm.role} onChange={handleEditChange}>
              <MenuItem value="prof">prof</MenuItem>
              <MenuItem value="formateur">formateur</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Annuler</Button>
          <Button type="submit" form="edit-teacher-form" variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}